import type { TOptions, TPlayer, TSource } from 'video.js'
import type { ExternalError } from '|>/components/error-display'
import type { TextTrackCustomSettings } from '|>/components/settings/subtitles/text-track-custom-settings'
import type { SkipSegment as SkipSegmentItem } from '|>/components/skip-segment/skip-segment.h'
import type { Configuration } from '|>/core'
import type { DolbyConfiguration as DolbyConfig } from '|>/core/addons/dolby'
import type { AdState } from '|>/core/addons/smartlib'
import type {
  MasterManifestData,
  TransmittedBytesData,
} from '|>/core/addons/stream-stats'
import type { Intent as IntentEnum } from '|>/shared/types/intent'
import type { MAX_AUTO_QUALITY } from '|>/shared/types/quality'
import type { AudioGroup } from './types/audio'

// import type { TAdMatcher } from '|>/components/ad-detection'

/**
 * Custom event names map
 * (maybe should contain native events as well)
 *
 * VideoJS can use any strings for event names, but strings are harder to find and refactor,
 * and also strings can be mistyped, so we use constants here.
 *
 * As a rule of thumb, all custom events names should be wrapped in `[]`
 * to distinguish them from native events and avoid conflicts.
 */
export const Events = {
  Media: {
    Load: '[media-load]',
    AudioTracksChanged: '[media-audio-tracks-changed]',
    AudioTrackChanged: '[media-audio-track-changed]',
    SubtitleTracksChanged: '[media-subtitle-tracks-changed]',
    SubtitleTrackChanged: '[media-subtitle-track-changed]',
    TitleChanged: '[media-title-changed]',
    ContentChanged: '[content-changed]',
    ProgressBarChanged: '[media-progress-bar-changed]',
    IsRewound: '[media-is-rewound]',
    SeekStep: '[media-seek-step]',
    IsRewindPending: '[media-is-rewind-pending]',
    RewindDetails: '[media-rewind-details]',
    IsAutoLiveTracker: '[media-is-live-seek-controlled]',
    // AdMatcher: '[media-ad-matcher]',
    // AdStarted: '[media-ad-started]',
    // AdEnded: '[media-ad-ended]',
    IsUrlPending: '[media-is-url-pending]',
    Error: '[media-error]',
    IsRenderedChildren: '[media-is-rendered-children]',
    QualityLevelSelected: '[media-quality-level-selected]',
    MaxAutoQuality: '[media-max-auto-quality]',
    DolbyTracks: '[media-dolby-tracks-found]',
    DolbyConfiguration: '[media-dolby-configuration]',
    AdStateChanged: '[media-ad-state-changed]',
    TimeUpdate: '[media-time-update]',
    PlaybackSessionId: '[media-playback-session-id]',
    MasterManifestDownloaded: '[media-master-manifest-downloaded]',
    TransmittedBytesInfo: '[media-transmitted-bytes-info]',
    SkipSegmentsChanged: '[media-skip-segments-changed]',
    IsPlayerDisabled: '[media-is-player-disabled]',
  },

  Controls: {
    SetPoster: '[controls-set-poster]',
    ShowPlayNextControl: '[controls-show-play-next]',
    ShowPlayNextEpisodeControl: '[controls-show-play-next-episode]',
    ShowPlayPreviousControl: '[controls-show-play-previous]',
    ShowInfoControl: '[controls-show-info]',
    ShowPlaylistControl: '[controls-show-playlist]',
    RegisterHotkey: '[controls-register-hotkey]',
  },

  Player: {
    Instantiation: '[player-new-instance]',
    IsSelectedQualityAllowed: '[player-is-selected-quality-allowed]',
    IsCastOptionAllowed: '[player-is-cast-option-allowed]',
    IsVideoQualityBlocked: '[player-is-video-quality-blocked]',
    UpdateOptions: '[player-update-options]',
    UpdateConfiguration: '[player-update-configuration]',
    Close: '[player-close]',
    LiveRewind: '[player-live-rewind]',
    DelayedLiveRewind: '[player-delayed-live-rewind]',
    GoLive: '[player-go-live]',
    GoLiveOnError: '[player-go-live-on-error]',
    Retry: '[player-retry]',
    PlayNext: '[player-play-next]',
    PlayNextEpisode: '[player-play-next-episode]',
    ProgramClick: '[player-program-click]',
    InfoClick: '[player-info-click]',
    PlaylistClick: '[player-playlist-click]',
    TogglePopup: '[player-toggle-popup]',
    RedirectTvClick: '[player-redirect-tv-click]',
    PlayPrevious: '[player-play-previous]',
    ProgressReachedEnd: '[player-progress-reached-end]',
    TokenExpired: '[player-token-expired]',
    ConcurrentStreamRetry: '[player-concurrent-stream-retry]',
    SmartRestartRetrying: '[player-smart-restart-retrying]',
    SmartRestartError: '[player-smart-restart-error]',
    SmartRestartSuccess: '[player-smart-restart-success]',
    SmartRestartAborted: '[player-smart-restart-aborted]',
    SkipIntro: '[player-skip-intro]',
    SkipCredits: '[player-skip-credits]',
    SkipSegment: '[player-skip-segment]',
  },

  TextTrack: {
    CustomSettingsUpdated: '[text-track-custom-settings-updated]',
    ManualSelection: '[text-track-manual-selection]',
  },

  // chromecast events, added by `@silvermine/videojs-chromecast` plugin
  // these are not our custom events, so they are not wrapped in brackets
  Chromecast: {
    Connected: 'chromecastConnected', // triggers when Chromecast connected
    Disconnected: 'chromecastDisconnected', // triggers when Chromecast disconnected
    DevicesAvailable: 'chromecastDevicesAvailable', // triggers on state change when Chromecast devices are available
    DevicesUnavailable: 'chromecastDevicesUnavailable', // triggers on state change when Chromecast devices are unavailable
    Requested: 'chromecastRequested', // triggers when the user has requested Chromecast playback using this plugin's Chromecast button
    Source: 'chromecastSource', // triggers when chromecast tech receives new source
  },

  User: {
    Intent: '[user-intent]',
  },
} as const

/**
 * Custom event payloads
 *
 * Defined as nested namespaces to make them look like a tree of events,
 * for example, payload for event `Events.Media.Load` has type `Events.Media.Load`
 */
export namespace Events {
  export namespace Media {
    export type MaxAutoQuality = MAX_AUTO_QUALITY | undefined
    export type DolbyTracks = AudioGroup[] | undefined
    export type DolbyConfiguration = DolbyConfig | undefined
    export type Load = TSource | undefined
    export type AudioTracksChanged = undefined
    export type AudioTrackChanged = undefined
    export type SubtitleTracksChanged = undefined
    export type SubtitleTrackChanged = undefined

    export type TitleChanged = {
      title?: string
      subtitle?: string
      logo?: string
    }
    export type ContentChanged = string | number | undefined
    export type ProgressBarChanged =
      | [number, number, number, number] // [start, seekStart, seekEnd, end]
      | [number, number, number, number, boolean?] // [start, seekStart, seekEnd, end, isInteractive = true]
      | null // disabled and non-interactive progress bar (but visible)
      | false // disabled and hidden progress bar
      | undefined // no data for live progressBar - vod progressBar showed
    export type IsRewound = boolean
    export type SeekStep = number | undefined
    export type IsRewindPending = boolean
    export type RewindDetails =
      | {
          rewoundTargetStartSec: number
          rewoundUrlStartSec: number
          rewindType?: string
        }
      | undefined
    export type IsAutoLiveTracker = boolean

    // numbers (code) defined here https://github.com/videojs/video.js/blob/d2b9d5c974036e637df133f38a95649ab2230490/src/js/media-error.js#L136
    // export type AdMatcher = TAdMatcher | undefined
    // export type AdStarted = undefined
    // export type AdEnded = undefined
    export type IsUrlPending = boolean | undefined
    export type Error = ExternalError | null | undefined
    export type IsRenderedChildren = boolean
    export type QualityLevelSelected = {
      id: string
      width: number
      height: number
    }
    export type AdStateChanged = AdState
    export type TimeUpdate = number
    export type PlaybackSessionId = string | undefined
    export type MasterManifestDownloaded = MasterManifestData
    export type TransmittedBytesInfo = TransmittedBytesData
    export type SkipSegmentsChanged = SkipSegmentItem[] | undefined
    export type IsPlayerDisabled = boolean
  }
  export namespace Controls {
    export type SetPoster =
      | { poster: string | undefined; fallback: string | undefined }
      | string
      | undefined
    export type ShowPlayNextControl = boolean
    export type ShowPlayNextEpisodeControl = boolean
    export type ShowPlayPreviousControl = boolean
    export type ShowInfoControl = boolean
    export type ShowPlaylistControl = boolean
    export type ArrayHotkey = [string | string[], () => void]
    export type ObjectHotkey = { key: string | string[]; handler: () => void }
    export type Hotkey = ArrayHotkey | ObjectHotkey
    export type RegisterHotkey = Hotkey | Hotkey[]
  }
  export namespace Player {
    export type Instantiation = { player: TPlayer }
    export type UpdateOptions = TOptions
    export type UpdateConfiguration = Configuration
    export type Retry = undefined
    export type Close = undefined
    export type LiveRewind = number | undefined
    export type DelayedLiveRewind = number | undefined
    export type GoLive = undefined
    export type PlayNext = undefined
    export type PlayNextEpisode = undefined
    export type ProgramClick = undefined
    export type InfoClick = undefined
    export type PlaylistClick = undefined
    export type TogglePopup = TogglePopupEvent
    export type RedirectTvClick = undefined
    export type PlayPrevious = undefined
    export type ProgressReachedEnd = undefined
    export type TokenExpired = undefined
    export type ConcurrentStreamRetry = undefined
    export type SmartRestartRetrying = undefined
    export type SmartRestartError = { cooldownMs: number }
    export type SmartRestartSuccess = undefined
    export type SmartRestartAborted = undefined
    export type SkipIntro = undefined
    export type SkipCredits = undefined
    export type SkipSegment = { segment: SkipSegmentItem }
    export type IsSelectedQualityAllowed = ({
      quality,
      manuallySelected,
    }: {
      quality: number
      manuallySelected?: boolean
    }) => boolean
    export type IsCastOptionAllowed = () => boolean
    export type IsVideoQualityBlocked = ({
      quality,
    }: {
      quality: number
    }) => boolean
  }

  export namespace TextTrack {
    export type CustomSettingsUpdated = TextTrackCustomSettings
    export type ManualSelection = {
      trackId: string
      isOff: boolean
    }
  }

  export namespace User {
    export type Intent = IntentEnum
  }
}

//
// Following types are utility types and should not be used in the code,
// aside from special cases for type inference, like in `useEventTrigger` and `useEventListener`
//

/**
 * Utility type to get the event payload type by event string name,
 * for example, `EventTypeMap[Events.Media.Load]` is `Events.Media.Load`
 * It is impossible to get the type by string directly in TypeScript, so we use this for mapping.
 */
// prettier-ignore
export type EventTypeMap = {
  [Events.Media.Load]: Events.Media.Load
  [Events.Media.AudioTracksChanged]: Events.Media.AudioTracksChanged
  [Events.Media.AudioTrackChanged]: Events.Media.AudioTrackChanged
  [Events.Media.SubtitleTracksChanged]: Events.Media.SubtitleTracksChanged
  [Events.Media.SubtitleTrackChanged]: Events.Media.SubtitleTrackChanged
  [Events.Media.TitleChanged]: Events.Media.TitleChanged
  [Events.Media.ContentChanged]: Events.Media.ContentChanged
  [Events.Media.ProgressBarChanged]: Events.Media.ProgressBarChanged
  [Events.Media.IsRewound]: Events.Media.IsRewound
  [Events.Media.SeekStep]: Events.Media.SeekStep
  [Events.Media.IsRewindPending]: Events.Media.IsRewindPending
  [Events.Media.RewindDetails]: Events.Media.RewindDetails
  [Events.Media.IsRenderedChildren]: Events.Media.IsRenderedChildren
  [Events.Media.IsAutoLiveTracker]: Events.Media.IsAutoLiveTracker
  [Events.Media.QualityLevelSelected]: Events.Media.QualityLevelSelected
  [Events.Media.MaxAutoQuality]: Events.Media.MaxAutoQuality
  [Events.Media.DolbyTracks]: Events.Media.DolbyTracks
  [Events.Media.DolbyConfiguration]: Events.Media.DolbyConfiguration
  [Events.Media.AdStateChanged]: Events.Media.AdStateChanged
  [Events.Media.TimeUpdate]: Events.Media.TimeUpdate
  [Events.Media.PlaybackSessionId]: Events.Media.PlaybackSessionId
  [Events.Media.MasterManifestDownloaded]: Events.Media.MasterManifestDownloaded
  [Events.Media.TransmittedBytesInfo]: Events.Media.TransmittedBytesInfo
  [Events.Media.SkipSegmentsChanged]: Events.Media.SkipSegmentsChanged
  [Events.Media.IsPlayerDisabled]: Events.Media.IsPlayerDisabled
  // [Events.Media.AdMatcher]: Events.Media.AdMatcher
  // [Events.Media.AdStarted]: Events.Media.AdStarted
  // [Events.Media.AdEnded]: Events.Media.AdEnded
  [Events.Media.IsUrlPending]: Events.Media.IsUrlPending
  [Events.Media.Error]: Events.Media.Error
  [Events.Controls.SetPoster]: Events.Controls.SetPoster
  [Events.Controls.ShowPlayNextControl]: Events.Controls.ShowPlayNextControl
  [Events.Controls.ShowPlayNextEpisodeControl]: Events.Controls.ShowPlayNextEpisodeControl
  [Events.Controls.ShowPlayPreviousControl]: Events.Controls.ShowPlayPreviousControl
  [Events.Controls.ShowInfoControl]: Events.Controls.ShowInfoControl
  [Events.Controls.ShowPlaylistControl]: Events.Controls.ShowPlaylistControl
  [Events.Controls.RegisterHotkey]: Events.Controls.RegisterHotkey
  [Events.Player.Instantiation]: Events.Player.Instantiation
  [Events.Player.Retry]: Events.Player.Retry
  [Events.Player.UpdateOptions]: Events.Player.UpdateOptions
  [Events.Player.UpdateConfiguration]: Events.Player.UpdateConfiguration
  [Events.Player.Close]: Events.Player.Close
  [Events.Player.LiveRewind]: Events.Player.LiveRewind
  [Events.Player.DelayedLiveRewind]: Events.Player.DelayedLiveRewind
  [Events.Player.GoLive]: Events.Player.GoLive
  [Events.Player.PlayNext]: Events.Player.PlayNext
  [Events.Player.PlayNextEpisode]: Events.Player.PlayNextEpisode
  [Events.Player.ProgramClick]: Events.Player.ProgramClick
  [Events.Player.InfoClick]: Events.Player.InfoClick
  [Events.Player.PlaylistClick]: Events.Player.PlaylistClick
  [Events.Player.TogglePopup]: Events.Player.TogglePopup
  [Events.Player.RedirectTvClick]: Events.Player.RedirectTvClick
  [Events.Player.PlayPrevious]: Events.Player.PlayPrevious
  [Events.Player.ProgressReachedEnd]: Events.Player.ProgressReachedEnd
  [Events.Player.TokenExpired]: Events.Player.TokenExpired
  [Events.Player.ConcurrentStreamRetry]: Events.Player.ConcurrentStreamRetry
  [Events.Player.SmartRestartRetrying]: Events.Player.SmartRestartRetrying
  [Events.Player.SmartRestartError]: Events.Player.SmartRestartError
  [Events.Player.SmartRestartSuccess]: Events.Player.SmartRestartSuccess
  [Events.Player.SmartRestartAborted]: Events.Player.SmartRestartAborted
  [Events.Player.SkipIntro]: Events.Player.SkipIntro
  [Events.Player.SkipCredits]: Events.Player.SkipCredits
  [Events.Player.SkipSegment]: Events.Player.SkipSegment
  [Events.Player.IsSelectedQualityAllowed]: Events.Player.IsSelectedQualityAllowed
  [Events.Player.IsCastOptionAllowed]: Events.Player.IsCastOptionAllowed
  [Events.Player.IsVideoQualityBlocked]: Events.Player.IsVideoQualityBlocked
  [Events.TextTrack.CustomSettingsUpdated]: Events.TextTrack.CustomSettingsUpdated
  [Events.TextTrack.ManualSelection]: Events.TextTrack.ManualSelection
  [Events.User.Intent]: Events.User.Intent
}

/**
 * Utility type to get the event payload type by event string name,
 * for example, `EventType<Events.Media.Load>` is `Events.Media.Load`
 */
export type EventType<T extends keyof EventTypeMap> = EventTypeMap[T]

export type TogglePopupEvent = { isOpen: boolean; className: string }
